import React from "react";
import { setTitle } from "../../../store/slices/navbar";
import { useAppDispatch, useAppSelector } from "../../../store";
import { get_user_profile } from "../../../service/api/user-profile";
import { get_company_details } from "../../../service/api/company";
import EditProfile from "../misc/EditProfile";
import PasswordChange from "../misc/PasswordChange";
import AnimatedPage from "../../../components/animation/AnimatedPage";

// Icons
import {
  IconMail,
  IconPhone,
  IconMapPin,
  IconId,
  IconShield,
  IconCheck,
  IconX,
  IconCamera,
} from "@tabler/icons-react";

const Setting = () => {
  const dispatch = useAppDispatch();
  const [update, updateSet] = React.useState<boolean>(false);
  const [password, passwordSet] = React.useState<boolean>(false);
  const user = useAppSelector(({ auth }) => auth.user);

  const onLoad = React.useCallback(() => {
    dispatch(setTitle("Settings"));
    get_user_profile(dispatch);
  }, [dispatch]);

  React.useEffect(() => {
    const fetchCompanyDetails = async () => {
      await get_company_details(dispatch);
    };
    fetchCompanyDetails();
  }, [dispatch]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "1":
        return {
          label: "Administrator",
          color: "bg-purple-100 text-purple-800",
        };
      case "2":
        return { label: "User", color: "bg-blue-100 text-blue-800" };
      default:
        return { label: "Guest", color: "bg-gray-100 text-gray-800" };
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "1":
        return {
          label: "Active",
          icon: <IconCheck size={14} />,
          color: "bg-primary-50/20 text-primary-600",
        };
      case "0":
        return {
          label: "Inactive",
          icon: <IconX size={14} />,
          color: "bg-red-100 text-red-800",
        };
      default:
        return {
          label: "Pending",
          icon: <IconX size={14} />,
          color: "bg-yellow-100 text-yellow-800",
        };
    }
  };

  const role = getRoleBadge(user?.role || "");
  const status = getStatusBadge(user?.status);

  const avatar =
    user?.profile.avatar ||
    "https://cdn.pixabay.com/photo/2020/05/17/20/21/cat-5183427_960_720.jpg";

  return (
    <AnimatedPage>
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-500 mt-1">Manage your personal information</p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100/20 overflow-hidden">
          {/* Banner Section */}
          <div className="relative h-32 bg-primary-100 sm:h-40">
            {user?.profile.banner ? (
              <img
                src={user.profile.banner}
                alt="Profile banner"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full pattern-isometric pattern-primary-600 pattern-bg-transparent pattern-opacity-20 pattern-size-8" />
            )}

            {/* Avatar */}
            <div className="absolute -bottom-12 left-6 sm:left-8">
              <div className="relative">
                <img
                  src={avatar}
                  alt={user?.profile.name}
                  className="w-24 h-24 rounded-2xl border-4 border-white object-cover shadow-lg"
                />
                <button className="absolute bottom-0 right-0 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors">
                  <IconCamera size={16} className="text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="pt-16 pb-6 px-6 sm:px-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {user?.profile.name || "Not set"}
                </h2>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${role.color}`}
                  >
                    <IconShield size={12} />
                    {role.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}
                  >
                    {status.icon}
                    {status.label}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => updateSet(true)}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200 hover:shadow-md active:scale-95"
                >
                  Edit Profile
                </button>
                <button
                  onClick={() => passwordSet(true)}
                  className="px-4 py-2 bg-gray-100/20 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-200 hover:shadow-md active:scale-95"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t border-gray-100/20 px-6 sm:px-8 py-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-gray-100/20 rounded-lg">
                  <IconMail size={20} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Email Address
                  </p>
                  <p className="text-sm text-gray-900 mt-1 break-all">
                    {user?.email || "Not set"}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-gray-100/20 rounded-lg">
                  <IconPhone size={20} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Phone Number
                  </p>
                  <p className="text-sm text-gray-900 mt-1">
                    {user?.profile.phone || "Not set"}
                  </p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-gray-100/20 rounded-lg">
                  <IconMapPin size={20} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Address
                  </p>
                  <p className="text-sm text-gray-900 mt-1">
                    {user?.profile.address || "Not set"}
                  </p>
                </div>
              </div>

              {/* NIN Number */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-gray-100/20 rounded-lg">
                  <IconId size={20} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    NIN Number
                  </p>
                  <p className="text-sm text-gray-900 mt-1">
                    {user?.profile.nin_number || "Not provided"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <PasswordChange open={password} onClose={() => passwordSet(false)} />
      <EditProfile open={update} onClose={() => updateSet(false)} />
    </AnimatedPage>
  );
};

export default Setting;
